<script>
import { mapActions, mapState, mapGetters } from 'vuex'
import ProductRoleModal from '../product/modals/create-role-modal.vue'
import DeleteModal from './modals/delete-modal'

export default {
  name: 'Role',
  components: {
    ProductRoleModal,
    DeleteModal,
  },
  props: {
    productId: {
      type: String,
      required: true,
    },
    roleId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      role: {},
      modals: {
        delete: false,
        edit: false,
      },
    }
  },
  computed: {
    ...mapState('product', ['product']),
    ...mapGetters('products', ['products']),
    ...mapGetters('me', ['canUpdateRole', 'canDeleteRole']),
    assignedProductName() {
      const { productId } = this
      const product = this.products.find(p => p.id === productId)

      if (product) {
        return product.name
      }

      return ''
    },
    assignedRoles() {
      return this.role.assignableRoles
    },
    backRoute() {
      return {
        name: 'product-details',
        params: { id: this.productId, tab: 'roles' },
      }
    },
    commands() {
      return [
        this.canDeleteRole && {
          command: 'delete',
          label: 'Delete Role',
        },
        this.canUpdateRole && {
          command: 'edit',
          label: 'Edit Role',
        },
      ]
    },
  },
  watch: {
    '$route.params.roleId'() {
      this.getData()
    },
  },
  async created() {
    this.getData()
  },
  methods: {
    ...mapActions('product', [
      'getProduct',
      'getRole',
    ]),
    async fetchRole() {
      const { productId, roleId } = this
      const [error, result] = await this.getRole({ productId, roleId })

      this.loading = true

      if (error) {
        this.$router.replace(this.backRoute)
      } else {
        this.role = result
        this.loading = false
      }
    },
    onEditClose() {
      this.fetchRole()
      this.modals.edit = false
    },
    handleCommand(command) {
      this.modals[command] = true
    },
    getData() {
      this.getProduct(this.productId)
      this.fetchRole()
    },
  },
}
</script>

<template>
  <main-layout
    :title="role.friendlyName"
    :back="backRoute"
    :loading="loading"
  >
    <template #header>
      <el-dropdown
        trigger="click"
        @command="handleCommand"
      >
        <dropdown-icon />
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item
            v-for="(command, index) in commands"
            :key="index"
            :command="command.command"
            :disabled="command.command === 'delete' && Boolean(assignedProductName)"
          >
            {{ command.label }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>

      <delete-modal
        v-if="modals.delete"
        :visible.sync="modals.delete"
      />
      <product-role-modal
        v-if="modals.edit"
        :visible.sync="modals.edit"
        is-edit
        :product-id="productId"
        :product-name="product.name"
        :role="role"
        :roles="product.roles"
        @close-modal="onEditClose"
      />
    </template>

    <el-card v-loading="loading">
      <div slot="header">
        Role Details
      </div>
      <div
        v-if="!loading"
        class="datalist"
      >
        <dt>Date Created</dt>
        <dd>{{ role.createdAt | dateTime }}</dd>

        <dt>Last Updated</dt>
        <dd>{{ role.updatedAt | dateTime }}</dd>

        <dt>Role ID</dt>
        <dd>{{ role.id }}</dd>

        <dt>Role Name</dt>
        <dd>{{ role.name }}</dd>

        <dt>Friendly Role Name</dt>
        <dd>{{ role.friendlyName }}</dd>

        <dt>Role Assignment Authority</dt>
        <dd>
          <router-link
            v-for="assignedRole in assignedRoles"
            :key="assignedRole.id"
            :class="$style.link"
            :to="{
              name: 'role',
              params: {
                productId: assignedRole.productId,
                roleId: assignedRole.id
              }
            }"
          >
            {{ assignedRole.friendlyName }}
          </router-link>
        </dd>

        <dt>Product</dt>
        <dd>
          <router-link
            :class="$style.link"
            :to="{
              name: 'product-details',
              params: {
                id: product.id,
              }
            }"
          >
            {{ product.name }}
          </router-link>
        </dd>
      </div>
    </el-card>
  </main-layout>
</template>

<style lang="scss" module>
.link {
  display: block;
  margin-bottom: rem(6px);
  text-decoration: underline;
}
</style>
